// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize } from "../utils";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";

export const SelfServiceView = ({ user = null, onNavigateToRequests = undefined }: { user?: UserRow | null; onNavigateToRequests?: () => void }) => {
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState("Thinking...");
  const [templateSelected, setTemplateSelected] = useState<any>(null);
  const [requestCreated, setRequestCreated] = useState<any>(null);
  const [form, setForm] = useState<Record<string, any>>({ counterparty: "", business_unit: "", description: "" });
  const [creating, setCreating] = useState(false);
  const [chatBusinessUnit, setChatBusinessUnit] = useState<any>(null);
  const [ssBusinessUnits, setSsBusinessUnits] = useState<any[]>([]);
  useEffect(() => { apiCall('/settings/business-units').then(r => { if (r?.businessUnits) setSsBusinessUnits(r.businessUnits); }); }, []);
  const [cpValidation, setCpValidation] = useState<any>(null); // { legal_name, address, confidence }
  const [cpValidating, setCpValidating] = useState(false);
  const [duplicates, setDuplicates] = useState<any[]>([]);
  const [dupeChecked, setDupeChecked] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const messagesEndRef = useRef<any>(null);
  const cpDebounce = useRef<any>(null);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading, suggestions]);

  // Auto-populate business unit from chat AI selection
  useEffect(() => {
    if (chatBusinessUnit) setForm((p: any) => ({ ...p, business_unit: chatBusinessUnit }));
  }, [chatBusinessUnit]);

  // Validate counterparty when user stops typing
  useEffect(() => {
    if (!form.counterparty || form.counterparty.trim().length < 3) { setCpValidation(null); setDuplicates([]); setDupeChecked(false); return; }
    clearTimeout(cpDebounce.current);
    cpDebounce.current = setTimeout(async () => {
      // Run duplicate check and counterparty validation in parallel
      setCpValidating(true);
      try {
        const [cpResult, dupeResult] = await Promise.all([
          apiCall("/self-service/validate-counterparty", { method: "POST", body: JSON.stringify({ name: form.counterparty.trim() }) }).catch((): any => null),
          apiCall("/self-service/check-duplicates", { method: "POST", body: JSON.stringify({ counterparty: form.counterparty.trim(), agreement_type: templateSelected?.agreement_type || null }) }).catch((): any => null)
        ]);
        if (cpResult) setCpValidation(cpResult);
        if (dupeResult?.duplicates) { setDuplicates(dupeResult.duplicates); setDupeChecked(true); }
      } finally {
        setCpValidating(false);
      }
    }, 800);
    return () => clearTimeout(cpDebounce.current);
  }, [form.counterparty, templateSelected]);

  const sendMessage = async (overrideText: string | undefined = undefined) => {
    const text = overrideText || input.trim();
    if (!text || loading) return;
    const userMsg = { role: "user", text };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput("");
    setSuggestions([]);
    setShowOtherInput(false);
    setLoading(true);
    try {
      const data = await apiCall("/self-service/chat", {
        method: "POST",
        body: JSON.stringify({ messages: updated.map(m => ({ role: m.role, content: m.text })) })
      });
      if (data?.response) {
        setMessages(prev => [...prev, { role: "assistant", text: data.response }]);
        if (data.template_selected) setTemplateSelected(data.template_selected);
        if (data.business_unit) setChatBusinessUnit(data.business_unit);
        setSuggestions(data.suggestions || []);
      } else {
        setMessages(prev => [...prev, {
          role: "assistant",
          text: "Sorry, I didn't get a response. The AI service may be temporarily busy — please try again in a moment."
        }]);
      }
    } catch (err: any) {
      console.error("Self-service chat error:", err);
      setMessages(prev => [...prev, {
        role: "assistant",
        text: "Network error. Please check your connection and try again."
      }]);
    }
    setLoading(false);
  };

  const createRequest = async () => {
    if (!form.counterparty) return;
    setCreating(true);
    try {
      const chatSummary = messages.map((m: any) => `${m.role === "user" ? "User" : "AI"}: ${m.text}`).join("\n\n");
      const cpName = cpValidation?.legal_name || form.counterparty;
      const cpAddr = cpValidation?.address || null;
      const autoTitle = templateSelected?.agreement_type || templateSelected?.name || 'Contract Request';
      const finalDescription = form.description?.trim() || ("Self-service request via AI questionnaire.\n\n" + chatSummary);
      console.log("[SelfService] Creating request:", { autoTitle, cpName, templateId: templateSelected?.id });
      const data = await apiCall("/self-service/create-request", {
        method: "POST",
        body: JSON.stringify({
          template_id: templateSelected?.id,
          title: autoTitle,
          counterparty: cpName,
          counterparty_address: cpAddr,
          counterparty_entity_type: cpValidation?.entity_type || null,
          counterparty_jurisdiction: cpValidation?.jurisdiction || null,
          counterparty_entity_designation: cpValidation?.entity_designation || null,
          description: `${finalDescription}\n\n--- Self-Service Questionnaire Summary ---\n\n${chatSummary}${cpAddr ? `\n\nCounterparty Address: ${cpAddr}` : ""}${cpValidation?.entity_designation ? `\nEntity Type: ${cpValidation.entity_designation}` : ""}`,
          business_unit: form.business_unit || null
        })
      });
      console.log("[SelfService] Create request response:", data);
      if (data?.request) setRequestCreated({ ...data.request, versionCreated: data.version_created });
      else alert("Failed to create request");
    } catch (err: any) {
      console.error("[SelfService] Create request error:", err);
      alert("Error creating request: " + (err.message || "Unknown error"));
    }
    setCreating(false);
  };

  // Escalate directly — create request and immediately escalate
  const escalateToQueue = async () => {
    if (!form.counterparty) return;
    setEscalating(true);
    const chatSummary = messages.map((m: any) => `${m.role === "user" ? "User" : "AI"}: ${m.text}`).join("\n\n");
    const cpName = cpValidation?.legal_name || form.counterparty;
    const cpAddr = cpValidation?.address || null;
    const data = await apiCall("/self-service/create-request", {
      method: "POST",
      body: JSON.stringify({
        template_id: templateSelected?.id,
        title: form.title || templateSelected?.name || "Contract Request",
        counterparty: cpName,
        counterparty_address: cpAddr,
        counterparty_entity_type: cpValidation?.entity_type || null,
        counterparty_jurisdiction: cpValidation?.jurisdiction || null,
        counterparty_entity_designation: cpValidation?.entity_designation || null,
        description: `Self-service questionnaire (escalated due to existing agreement):\n\n${chatSummary}${cpAddr ? `\n\nCounterparty Address: ${cpAddr}` : ""}${cpValidation?.entity_designation ? `\nEntity Type: ${cpValidation.entity_designation}` : ""}`
      })
    });
    if (data?.request) {
      await apiCall(`/self-service/escalate/${data.request.id}`, { method: "POST" });
      setRequestCreated({ ...data.request, escalated: true });
    } else { alert("Failed to create request"); }
    setEscalating(false);
  };

  const hasDuplicates = duplicates.length > 0;
  const inp: CSSProperties = { width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box" };

  if (requestCreated) {
    return (
      <div style={{ flex: 1, overflow: "auto", padding: "32px 40px", maxWidth: 700, margin: "0 auto" }}>
        <Card style={{ padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>&#10003;</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.tx, marginBottom: 8 }}>{requestCreated.escalated ? "Request Escalated" : "Request Created"}</div>
          <div style={{ fontSize: 13, color: T.txM, marginBottom: 4 }}>"{requestCreated.title}" with {requestCreated.counterparty}</div>
          <div style={{ fontSize: 12, color: T.txD, marginBottom: 20 }}>{requestCreated.escalated ? "Your request has been sent to the appropriate team for review and assignment." : requestCreated.versionCreated ? "A draft has been prepared with counterparty details populated. You can view and download it from the Versions tab in the Requests page, or escalate for review." : "Status: Self-Service Draft. You can view this request from the Requests page, or escalate for review."}</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <Btn v="primary" onClick={() => { setRequestCreated(null); setMessages([]); setTemplateSelected(null); setForm({ title: "", counterparty: "" }); setCpValidation(null); setDuplicates([]); setDupeChecked(false); }}>Start New</Btn>
          </div>
        </Card>
      </div>
    );
  }

  const starterSuggestions = [
    "I need to set up a new vendor agreement",
    "We're signing an NDA with a potential partner",
    "I need a services agreement for a new project"
  ];

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.brd}` }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.tx }}>Self-Service Contract Request</div>
        <div style={{ fontSize: 12, color: T.txM }}>Answer a few questions and we'll find the right contract template for you.</div>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px" }}>
        {messages.length === 0 && !loading && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>&#9830;</div>
            <div style={{ fontSize: 14, color: T.txM, marginBottom: 20 }}>Tell us about the contract you need. Try one of these:</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 400, margin: "0 auto" }}>
              {starterSuggestions.map((s: any, i: number) => (
                <button key={i} onClick={() => sendMessage(s)} style={{ padding: "10px 16px", fontSize: 13, color: T.acc, backgroundColor: T.accS, border: `1px solid ${T.acc}22`, borderRadius: 8, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>{s}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m: any, i: number) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 12 }}>
            <div style={{ maxWidth: "75%", padding: "10px 14px", borderRadius: 12, fontSize: 13, lineHeight: 1.5, color: m.role === "user" ? "#fff" : T.tx, backgroundColor: m.role === "user" ? T.acc : T.bg3, border: m.role === "user" ? "none" : `1px solid ${T.brd}`, whiteSpace: "pre-wrap" }}>{m.text}</div>
          </div>
        ))}

        {loading && (
          <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 12 }}>
            <div style={{ padding: "10px 14px", borderRadius: 12, fontSize: 13, color: T.txM, backgroundColor: T.bg3, border: `1px solid ${T.brd}` }}>{loadingStatus}</div>
          </div>
        )}

        {/* Suggested answer buttons */}
        {suggestions.length > 0 && !loading && !templateSelected && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12, justifyContent: "flex-end" }}>
            {suggestions.map((s: any, i: number) => {
              const isOther = s.toLowerCase().includes("other") || s.toLowerCase().includes("something else") || s.toLowerCase().includes("none of");
              return isOther ? (
                <button key={i} onClick={() => setShowOtherInput(true)} style={{ padding: "8px 14px", fontSize: 12, color: T.txM, backgroundColor: "transparent", border: `1px dashed ${T.brd}`, borderRadius: 16, cursor: "pointer", fontFamily: "inherit" }}>{s}</button>
              ) : (
                <button key={i} onClick={() => sendMessage(s)} style={{ padding: "8px 14px", fontSize: 12, color: T.acc, backgroundColor: T.accS, border: `1px solid ${T.acc}22`, borderRadius: 16, cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }}>{s}</button>
              );
            })}
          </div>
        )}
        {showOtherInput && !loading && (
          <div style={{ display: "flex", gap: 6, marginBottom: 12, justifyContent: "flex-end", alignItems: "center" }}>
            <VoiceInput onTranscript={(text: string) => setInput(text)} disabled={false} />
            <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && sendMessage()} placeholder="Type your answer..." autoFocus style={{ padding: "8px 12px", borderRadius: 16, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: "inherit", minWidth: 200 }} />
            <button onClick={() => sendMessage()} disabled={!input.trim()} style={{ padding: "8px 14px", fontSize: 12, color: "#fff", backgroundColor: T.acc, border: "none", borderRadius: 16, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, opacity: input.trim() ? 1 : 0.5 }}>Send</button>
            <button onClick={() => { setShowOtherInput(false); setInput(""); }} style={{ padding: "8px 10px", fontSize: 12, color: T.txD, backgroundColor: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          </div>
        )}

        {templateSelected && !requestCreated && (
          <Card style={{ marginTop: 16, padding: 16, border: `2px solid ${T.grn}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <Badge c={T.grn} b={T.grnS}>Template Found</Badge>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.tx }}>{templateSelected.name}</span>
            </div>
            <div style={{ fontSize: 12, color: T.txM, marginBottom: 16 }}>Type: {templateSelected.agreement_type}</div>

            {/* Counterparty input */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.txD, marginBottom: 4 }}>Counterparty Name *</label>
              <input value={form.counterparty} onChange={e => setForm((p: any) => ({ ...p, counterparty: e.target.value }))} placeholder="Enter the other party's name..." style={inp} />
              {cpValidating && <div style={{ fontSize: 10, color: T.acc, marginTop: 4 }}>Validating counterparty...</div>}

              {/* AI-validated counterparty info */}
              {cpValidation && !cpValidating && form.counterparty.length >= 3 && (
                <div style={{ marginTop: 6, padding: "8px 12px", backgroundColor: cpValidation.confidence === "high" ? "rgba(43,122,62,.06)" : "rgba(208,164,64,.06)", border: `1px solid ${cpValidation.confidence === "high" ? "rgba(43,122,62,.15)" : "rgba(208,164,64,.15)"}`, borderRadius: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <Badge c={cpValidation.confidence === "high" ? T.grn : T.ylw} b={cpValidation.confidence === "high" ? T.grnS : T.ylwS}>{cpValidation.confidence} confidence</Badge>
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.tx }}>{cpValidation.legal_name}</span>
                    {cpValidation.legal_name !== form.counterparty && (
                      <button onClick={() => setForm((p: any) => ({ ...p, counterparty: cpValidation.legal_name }))} style={{ fontSize: 10, color: T.acc, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Use this name</button>
                    )}
                  </div>
                  {cpValidation.entity_designation && <div style={{ fontSize: 11, color: T.tx, marginBottom: 2 }}>{cpValidation.entity_designation}</div>}
                  {cpValidation.address && <div style={{ fontSize: 11, color: T.txM }}>{cpValidation.address}</div>}
                  {cpValidation.notes && <div style={{ fontSize: 10, color: T.txD, marginTop: 2 }}>{cpValidation.notes}</div>}
                </div>
              )}
            </div>

            {/* Duplicate warning */}
            {hasDuplicates && dupeChecked && (
              <div style={{ marginBottom: 12, padding: "12px 14px", backgroundColor: "rgba(208,64,64,.06)", border: "1px solid rgba(208,64,64,.15)", borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.red, marginBottom: 6 }}>Existing agreement(s) found</div>
                <div style={{ fontSize: 11, color: T.txM, marginBottom: 8 }}>A similar agreement with this counterparty already exists. Self-service is not available for this request. Please escalate to the appropriate team for assistance.</div>
                {duplicates.map((d: any, i: number) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 11, borderBottom: i < duplicates.length - 1 ? `1px solid ${T.brdSubtle}` : "none" }}>
                    <Badge c={d.type === "request" ? T.orn : T.blu} b={d.type === "request" ? T.ornS : T.bluS}>{d.type === "request" ? "Pending Request" : "Executed"}</Badge>
                    <span style={{ color: T.tx, fontWeight: 500 }}>{d.title}</span>
                    <span style={{ color: T.txD }}>({d.counterparty})</span>
                    {d.status && d.status !== "executed" && <span style={{ color: T.txD }}>- {d.status.replace(/_/g, " ")}</span>}
                  </div>
                ))}
                <div style={{ marginTop: 10 }}>
                  <Btn v="primary" disabled={escalating} onClick={() => { escalateToQueue(); }} style={{ backgroundColor: T.orn, borderColor: T.orn }}>
                    {escalating ? "Escalating..." : "Escalate to Review Queue"}
                  </Btn>
                </div>
              </div>
            )}

            {/* Form fields after template match */}
            {!hasDuplicates && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.txD, marginBottom: 4 }}>Description <span style={{ fontWeight: 400, color: T.txM }}>(optional — auto-generated from chat if blank)</span></label>
                  <textarea value={form.description || ""} onChange={e => setForm((p: any) => ({ ...p, description: e.target.value }))} placeholder="Describe the scope and purpose of this agreement..." rows={3} style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box", resize: "vertical" }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.txD, marginBottom: 4 }}>Business Unit</label>
                  <select value={form.business_unit || ""} onChange={e => setForm((p: any) => ({ ...p, business_unit: e.target.value }))} style={{ ...inp, cursor: "pointer" }}><option value="">Select...</option>{ssBusinessUnits.map(bu => <option key={bu.id} value={bu.name}>{bu.name}</option>)}</select>
                </div>
                <Btn v="primary" disabled={!form.counterparty || creating || cpValidating} onClick={() => { createRequest(); }}>{creating ? "Preparing draft..." : "Create Request"}</Btn>
              </>
            )}
          </Card>
        )}

        <div ref={messagesEndRef} />
      </div>

      {(suggestions.length === 0 || showOtherInput || messages.length === 0) && !templateSelected && (
        <div style={{ padding: "12px 24px", borderTop: `1px solid ${T.brd}`, display: "flex", gap: 8, alignItems: "center" }}>
          <VoiceInput onTranscript={(text: string) => setInput(text)} disabled={loading} />
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && sendMessage()} placeholder={suggestions.length > 0 ? "Type your answer..." : "Describe what you need..."} disabled={loading} style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13, outline: "none", fontFamily: "inherit" }} />
          <Btn v="primary" disabled={!input.trim() || loading} onClick={() => sendMessage()}>Send</Btn>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════ SELF-SERVICE TEMPLATE MANAGER (ADMIN) ═══════════════════ */
